import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Building2, Mail, Phone, MapPin, ArrowRight, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PracticeSignup = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);
  const [signupResult, setSignupResult] = useState(null);
  const [formError, setFormError] = useState('');
  const [subdomainStatus, setSubdomainStatus] = useState({ checking: false, available: null, message: '' });
  const [formData, setFormData] = useState({
    practiceName: '',
    ownerName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    subdomain: '',
    description: ''
  });

  // Check subdomain availability with debounce
  const checkSubdomainAvailability = useCallback(async (subdomain) => {
    if (!subdomain || subdomain.length < 3) {
      setSubdomainStatus({ checking: false, available: null, message: '' });
      return;
    }
    
    setSubdomainStatus({ checking: true, available: null, message: 'Checking...' });
    
    try {
      const response = await axios.get(`${API}/practices/check-subdomain/${subdomain}`);
      setSubdomainStatus({ 
        checking: false, 
        available: response.data.available, 
        message: response.data.available ? 'Available!' : 'Already taken'
      });
    } catch (error) {
      setSubdomainStatus({ checking: false, available: null, message: '' });
    }
  }, []);

  // Debounce subdomain check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.subdomain) {
        checkSubdomainAvailability(formData.subdomain);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.subdomain, checkSubdomainAvailability]);

  const handleChange = (field, value) => {
    // Clear form error when user makes changes
    setFormError('');
    setFormData({ ...formData, [field]: value });
    
    // Auto-generate subdomain from practice name
    if (field === 'practiceName') {
      const subdomain = value.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 20);
      setFormData({ ...formData, [field]: value, subdomain });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormError('');
    
    try {
      const response = await axios.post(`${API}/practices/signup`, formData);
      
      setSignupResult(response.data);
      setSignupComplete(true);
      
      toast({
        title: 'Practice Created!',
        description: `Welcome to MdSpa360! Your practice has been set up.`
      });
    } catch (error) {
      console.error('Signup error:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to create practice. Please try again.';
      
      // Set inline form error
      setFormError(errorMessage);
      
      // Also show toast
      toast({
        title: 'Signup Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Show success screen with credentials
  if (signupComplete && signupResult) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-lg w-full">
          <Card className="shadow-lg">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl text-green-700">Welcome to MdSpa360!</CardTitle>
              <CardDescription>Your practice has been created successfully</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Your Practice</h4>
                <p className="text-blue-700">{signupResult.practice?.practiceName}</p>
                <p className="text-sm text-blue-600">Practice ID: {signupResult.practice?.practiceId}</p>
              </div>
              
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <h4 className="font-semibold text-yellow-800 mb-2">Your Admin Login Credentials</h4>
                <p className="text-sm text-yellow-700 mb-2">Save these! You'll need them to log in.</p>
                <div className="bg-white rounded p-3 space-y-2">
                  <p><strong>Email:</strong> {signupResult.admin?.email}</p>
                  <p><strong>Temporary Password:</strong> <code className="bg-gray-100 px-2 py-1 rounded">{signupResult.admin?.temporaryPassword}</code></p>
                </div>
                <p className="text-xs text-yellow-600 mt-2">Please change your password after logging in.</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-700 mb-2">Next Steps</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {signupResult.nextSteps?.map((step, idx) => (
                    <li key={idx}>✓ {step}</li>
                  ))}
                </ul>
              </div>
            </CardContent>
            <CardFooter>
              <Button className="w-full" onClick={() => navigate('/login')}>
                Go to Login <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="h-8 w-8 text-gray-900" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Start Your Practice on MdSpa360
          </h1>
          <p className="text-lg text-gray-600">
            Set up your white-labeled booking platform in minutes
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-4">
            {[1, 2].map((s) => (
              <React.Fragment key={s}>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                    step >= s ? 'bg-primary text-gray-900' : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {s}
                </div>
                {s < 2 && (
                  <div className={`w-16 h-1 transition-colors ${step > s ? 'bg-primary' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <Card className="shadow-lg">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Practice Information */}
            {step === 1 && (
              <>
                <CardHeader>
                  <CardTitle className="text-2xl">Practice Information</CardTitle>
                  <CardDescription>Tell us about your medical spa</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label htmlFor="practiceName">Practice Name *</Label>
                    <Input
                      id="practiceName"
                      value={formData.practiceName}
                      onChange={(e) => handleChange('practiceName', e.target.value)}
                      placeholder="Acme Med Spa"
                      required
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="ownerName">Owner Name *</Label>
                    <Input
                      id="ownerName"
                      value={formData.ownerName}
                      onChange={(e) => handleChange('ownerName', e.target.value)}
                      placeholder="Dr. Jane Smith"
                      required
                      className="mt-2"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="hello@acmemedspa.com"
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="(555) 123-4567"
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="description">Practice Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Premium aesthetic and wellness treatments..."
                      rows={3}
                      className="mt-2"
                    />
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    type="button"
                    className="w-full"
                    onClick={() => setStep(2)}
                    disabled={!formData.practiceName || !formData.ownerName || !formData.email || !formData.phone}
                  >
                    Continue <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardFooter>
              </>
            )}

            {/* Step 2: Location & Domain */}
            {step === 2 && (
              <>
                <CardHeader>
                  <CardTitle className="text-2xl">Location & Domain</CardTitle>
                  <CardDescription>Where patients can find you</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label htmlFor="address">Street Address *</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      placeholder="123 Wellness Ave, Suite 100"
                      required
                      className="mt-2"
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => handleChange('city', e.target.value)}
                        placeholder="Los Angeles"
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => handleChange('state', e.target.value)}
                        placeholder="CA"
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zipCode">Zip Code *</Label>
                      <Input
                        id="zipCode"
                        value={formData.zipCode}
                        onChange={(e) => handleChange('zipCode', e.target.value)}
                        placeholder="90001"
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subdomain">Your Booking URL *</Label>
                    <div className="flex gap-2 mt-2">
                      <div className="relative flex-1">
                        <Input
                          id="subdomain"
                          value={formData.subdomain}
                          onChange={(e) => handleChange('subdomain', e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                          placeholder="acmemedspa"
                          required
                          className={subdomainStatus.available === false ? 'border-red-500' : subdomainStatus.available === true ? 'border-green-500' : ''}
                        />
                        {subdomainStatus.checking && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                        )}
                        {!subdomainStatus.checking && subdomainStatus.available === true && (
                          <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                        )}
                        {!subdomainStatus.checking && subdomainStatus.available === false && (
                          <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />
                        )}
                      </div>
                      <div className="flex items-center text-gray-500 whitespace-nowrap">.mdspa360.com</div>
                    </div>
                    <div className="flex justify-between mt-1">
                      <p className="text-sm text-gray-500">
                        Your patients will book at: <strong>{formData.subdomain || 'yourpractice'}.mdspa360.com</strong>
                      </p>
                      {subdomainStatus.message && (
                        <p className={`text-sm ${subdomainStatus.available ? 'text-green-600' : 'text-red-600'}`}>
                          {subdomainStatus.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Error message */}
                  {formError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                      <p className="font-medium">Error</p>
                      <p className="text-sm">{formError}</p>
                    </div>
                  )}

                  <div className="bg-secondary rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">What's Next?</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>✓ Customize your branding and colors</li>
                      <li>✓ Add your treatments and pricing</li>
                      <li>✓ Create membership plans</li>
                      <li>✓ Start accepting bookings!</li>
                    </ul>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between">
                  <Button type="button" variant="outline" onClick={() => { setStep(1); setFormError(''); }}>
                    Back
                  </Button>
                  <Button type="submit" disabled={loading || subdomainStatus.available === false}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : 'Create Practice'}
                  </Button>
                </CardFooter>
              </>
            )}
          </form>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          Already have a practice? <a href="/admin/login" className="text-primary hover:underline">Log in</a>
        </p>
      </div>
    </div>
  );
};

export default PracticeSignup;